/**
 * Icon lookup for the data-driven ribbon.
 *
 * Ribbon items name their icon as a string, so the renderer needs a
 * name → component map. We register icons explicitly instead of
 * `import * as lucide` because a namespace import defeats tree-shaking and
 * pulls all ~1500 lucide icons into the bundle. The cost of that choice is one
 * line here when a v2 tab introduces a new icon; the fallback below means
 * forgetting that line renders a placeholder rather than crashing the shell.
 */

import {
  AlignCenterVertical,
  AlignHorizontalJustifyCenter,
  AlignVerticalJustifyCenter,
  Bell,
  Bookmark,
  Box,
  Boxes,
  Brain,
  Camera,
  ChartLine,
  ChevronDown,
  ChevronRight,
  Circle,
  CirclePlay,
  CircleQuestionMark,
  ClipboardCopy,
  Clock,
  CloudDownload,
  Columns3,
  Compass,
  Container,
  Contrast,
  Copy,
  Cpu,
  Crop,
  Crosshair,
  Download,
  Droplet,
  Eraser,
  Eye,
  FileClock,
  FileCode2,
  FileDigit,
  FileImage,
  FileInput,
  FileJson,
  FilePlus,
  FilePlus2,
  FileText,
  FileType2,
  FileWarning,
  Files,
  FlipHorizontal2,
  FolderCog,
  FolderInput,
  FolderOpen,
  FolderSearch,
  FolderX,
  Forward,
  Gauge,
  GitCompare,
  GitMerge,
  Grid2x2,
  Hand,
  HardDrive,
  History,
  Image,
  Images,
  Info,
  Layers,
  Layers2,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  ListChecks,
  ListTodo,
  LogOut,
  Map,
  Maximize2,
  MemoryStick,
  Minimize2,
  Move,
  Navigation,
  Package,
  Palette,
  PanelBottom,
  PanelLeft,
  PanelRight,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Ruler,
  Save,
  SaveAll,
  Scaling,
  Scan,
  Scissors,
  ScrollText,
  Search,
  Settings,
  Share2,
  Sheet,
  ShieldCheck,
  Shuffle,
  SlidersHorizontal,
  Sparkles,
  Spline,
  Square,
  SquarePen,
  StickyNote,
  Sun,
  Table2,
  Target,
  Terminal,
  Timer,
  ToggleLeft,
  Trash2,
  Undo2,
  Waves,
  Workflow,
  Zap,
  ZoomIn,
  type LucideIcon,
} from "lucide-react";

const ICONS: Record<string, LucideIcon> = {
  AlignCenterVertical,
  AlignHorizontalJustifyCenter,
  AlignVerticalJustifyCenter,
  Bell,
  Bookmark,
  Box,
  Boxes,
  Brain,
  Camera,
  ChartLine,
  ChevronDown,
  ChevronRight,
  Circle,
  CirclePlay,
  ClipboardCopy,
  Clock,
  CloudDownload,
  Columns3,
  Compass,
  Container,
  Contrast,
  Copy,
  Cpu,
  Crop,
  Crosshair,
  Download,
  Droplet,
  Eraser,
  Eye,
  FileClock,
  FileCode2,
  FileDigit,
  FileImage,
  FileInput,
  FileJson,
  FilePlus,
  FilePlus2,
  FileText,
  FileType2,
  FileWarning,
  Files,
  FlipHorizontal2,
  FolderCog,
  FolderInput,
  FolderOpen,
  FolderSearch,
  FolderX,
  Forward,
  Gauge,
  GitCompare,
  GitMerge,
  Grid2x2,
  Hand,
  HardDrive,
  History,
  Image,
  Images,
  Info,
  Layers,
  Layers2,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  ListChecks,
  ListTodo,
  LogOut,
  Map,
  Maximize2,
  MemoryStick,
  Minimize2,
  Move,
  Navigation,
  Package,
  Palette,
  PanelBottom,
  PanelLeft,
  PanelRight,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Ruler,
  Save,
  SaveAll,
  Scaling,
  Scan,
  Scissors,
  ScrollText,
  Search,
  Settings,
  Share2,
  Sheet,
  ShieldCheck,
  Shuffle,
  SlidersHorizontal,
  Sparkles,
  Spline,
  Square,
  SquarePen,
  StickyNote,
  Sun,
  Table2,
  Target,
  Terminal,
  Timer,
  ToggleLeft,
  Trash2,
  Undo2,
  Waves,
  Workflow,
  Zap,
  ZoomIn,
};

/** Shown when a config names an icon we do not carry. Never throws. */
export const FALLBACK_ICON: LucideIcon = CircleQuestionMark;

export function resolveIcon(name: string): LucideIcon {
  const icon = ICONS[name];
  if (icon) return icon;
  if (import.meta.env.DEV) console.warn(`[ribbon] unknown icon "${name}" — using fallback`);
  return FALLBACK_ICON;
}

/** Escape hatch for a plugin/v2 tab that ships its own icons. */
export function registerIcon(name: string, icon: LucideIcon): void {
  ICONS[name] = icon;
}
